import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "motion/react";
import RabbitProgress from "../components/RabbitProgress";

const AY_OPTIONS = ["AY2026-27", "AY2025-26"];

export default function CapitalGain() {
  const { t } = useTranslation();
  const [files, setFiles] = useState<File[]>([]);
  const [ay, setAy] = useState("AY2026-27");
  const [clientName, setClientName] = useState("");
  const [fy, setFy] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(prev => [...prev, ...Array.from(e.target.files as FileList)]);
    }
  };

  const removeFile = (idx: number) => {
    setFiles(prev => prev.filter((_: File, i: number) => i !== idx));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError("Please select at least one file"); return;
    }
    setLoading(true); setError(""); setSuccess(false);
    const formData = new FormData();
    files.forEach((f: File) => formData.append("files", f));
    formData.append("assessment_year", ay);
    formData.append("client_name", clientName);
    formData.append("financial_year", fy);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/api/capital-gain/calculate", {
        method: "POST",
        headers: token ? { Authorization: "Bearer " + token } : {},
        body: formData,
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Processing failed");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "TaxThinker_CapitalGain_" + (clientName || "Report") + "_" + ay + ".xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      (window as any).gtag?.('event', 'report_generated', {
        report_type: 'capital_gain',
        assessment_year: ay,
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="page-header-dark">
        <div className="ph-dotgrid" />
        <div className="ph-glow" />
        <div className="ph-inner">
          <div className="ph-breadcrumb">
            <a href="/">Home</a>
            <span>›</span>
            <span>Tools</span>
            <span>›</span>
            <span style={{ color: "#C9A84C" }}>Capital Gain Calculator</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "8px" }}>
            <span style={{ fontSize: "36px" }}>📈</span>
            <h1>{t("capitalGain.title")}</h1>
          </div>
          <p className="ph-subtitle">{t("capitalGain.subtitle")}</p>
        </div>
      </div>

    <div className="max-w-4xl mx-auto px-4 py-12">

      <div className="bg-white rounded-2xl shadow-sm border p-8 space-y-6">

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              {t("capitalGain.assessmentYear")}
            </label>
            <select
              value={ay}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAy(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              {AY_OPTIONS.map((o: string) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              {t("capitalGain.clientName")}
            </label>
            <input
              type="text"
              value={clientName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setClientName(e.target.value)}
              placeholder="e.g. Ramesh Kumar"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              {t("capitalGain.financialYear")}
            </label>
            <input
              type="text"
              value={fy}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFy(e.target.value)}
              placeholder="e.g. 2025-26"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            {t("capitalGain.uploadSubtitle")}
          </label>
          <div
            className="upload-area-premium"
            onClick={() => document.getElementById("cgFileInput")!.click()}
          >
            <input
              id="cgFileInput"
              type="file"
              multiple
              accept=".pdf,.xlsx,.xls,.csv"
              className="hidden"
              onChange={handleFiles}
            />
            <div className="upload-icon-bounce" style={{ marginBottom: "12px" }}>
              <svg width="44" height="44" viewBox="0 0 44 44" fill="none" style={{ display: "inline-block" }}>
                <path d="M22 8L22 28M22 8L14 16M22 8L30 16" stroke="#C9A84C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8 32V36H36V32" stroke="rgba(201,168,76,0.4)" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            </div>
            <p style={{ fontWeight: 700, color: "var(--text-primary)", marginBottom: "6px", fontSize: "18px" }}>Drop your files here</p>
            <p style={{ fontSize: "14px", color: "#C9A84C", textDecoration: "underline", marginBottom: "8px" }}>or click to browse</p>
            <p style={{ fontSize: "12px", color: "var(--text-muted)" }}>PDF, Excel, CSV up to 10MB · Multiple files supported</p>
          </div>
          {/* Trust badges */}
          <div style={{ display: "flex", gap: "16px", justifyContent: "center", flexWrap: "wrap", marginTop: "12px" }}>
            {["🔒 Encrypted", "⚡ Processed instantly", "✓ CA-verified format"].map(b => (
              <span key={b} style={{ fontSize: "12px", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: "4px" }}>{b}</span>
            ))}
          </div>
        </div>

        <AnimatePresence>
          {files.length > 0 && (
            <motion.div
              key="file-list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-2"
            >
              <p className="text-sm font-semibold text-gray-700">{t("capitalGain.filesSelected")} ({files.length}):</p>
              <AnimatePresence>
                {files.map((f: File, idx: number) => (
                  <motion.div
                    key={`${f.name}-${idx}`}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2 border"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{f.name.endsWith(".pdf") ? "📄" : "📊"}</span>
                      <div>
                        <p className="text-sm font-medium text-gray-700">{f.name}</p>
                        <p className="text-xs text-gray-400">{(f.size / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                    <button onClick={() => removeFile(idx)} className="text-red-400 hover:text-red-600 text-sm">{t("capitalGain.remove")}</button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
        )}

        <AnimatePresence>
          {success && (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="px-4 py-3 rounded-lg text-sm"
              style={{ background: "#E2EFDA", border: "1px solid #A9C9A0", color: "#375623", boxShadow: "0 0 12px rgba(46,125,50,0.15)" }}
            >
              {t("capitalGain.success")}
            </motion.div>
          )}
        </AnimatePresence>

        <motion.button
          onClick={handleUpload}
          disabled={files.length === 0 || loading}
          animate={loading ? { opacity: [1, 0.7, 1] } : { opacity: 1 }}
          transition={loading ? { repeat: Infinity, duration: 1.5 } : {}}
          whileHover={!loading && files.length > 0 ? { scale: 1.02 } : {}}
          whileTap={!loading && files.length > 0 ? { scale: 0.98 } : {}}
          className="w-full py-3 rounded-xl font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: "#C9A84C", color: "#2C2C2C", border: "none", cursor: files.length > 0 && !loading ? "pointer" : "not-allowed" }}
        >
          {loading ? t("capitalGain.generating") : t("capitalGain.generate")}
        </motion.button>

        <RabbitProgress loading={loading} estimatedSeconds={60} />

        <div className="bg-blue-50 rounded-xl p-5">
          <p className="text-sm font-semibold text-blue-800 mb-3">{t("capitalGain.sheetsTitle")}</p>
          <div className="grid md:grid-cols-2 gap-2 text-sm text-blue-700">
            <div>Sheet 1 — All transactions in detail</div>
            <div>Sheet 2 — Capital Gain Summary (scheme-wise)</div>
            <div>Sheet 3 — Tax Calculation (STCG + LTCG)</div>
            <div>Sheet 4 — Financials (F&O / Intraday P&L)</div>
            <div className="md:col-span-2">Sheet 5 — How to report in ITR Utility, Computax and Winman</div>
          </div>
        </div>

        {ay === "AY2025-26" && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm text-yellow-800">
            <strong>AY 2025-26 selected:</strong> Tax rates will be split — old rates (STCG 15% / LTCG 10% / exemption Rs 1 lakh) for transactions before 23 July 2024, and new rates (STCG 20% / LTCG 12.5% / exemption Rs 1.25 lakh) for transactions on or after 23 July 2024.
          </div>
        )}
      </div>
    </div>
    </div>
  );
}
